import React, { CSSProperties } from 'react';

import arrowImg from 'assets/images/arrow-yellow.png';
import percentImg from 'assets/images/percent.png';
import functionalConnection from 'assets/images/svg/connection-functional.svg';
import functionalConnectionMobile from 'assets/images/svg/connection-functional-mobile.svg';
import diagram from 'assets/images/svg/conomica-diagram.svg';
import diagramMobile from 'assets/images/svg/conomica-diagram-mobile.svg';
import fundsMovement from 'assets/images/svg/funds-movement.svg';

import { Container, Image, Section } from '../../../components';
import { useWindowWidth } from '../../../components/pages/detailed-project/hooks';
import { useRevealOnScroll } from '../../../hooks';
import styles from './HowItWorks.module.scss';

const steps = [
  {
    n: 1,
    title: 'Регистрация на платформе',
    text: 'Занимает несколько минут, пользователь задаёт логин/пароль доступа.',
  },
  {
    n: 2,
    title: 'Верификация',
    text: 'Пользователь заполняет анкету с данными, необходимыми для установления отношений гражданско-правового характера.',
  },
  {
    n: 3,
    title: 'Согласие с правилами платформы',
    text: 'Ознакомление и подтверждение согласия с условиями пользовательского договора.',
  },
  {
    n: 4,
    title: 'Заключение агентского договора',
    text: 'Принятие Оферты о заключении договора с агентом-взыскателем, который будет приобретать долговые требования и осуществлять взыскание, действуя в интересах инвестора.',
  },
  {
    n: 5,
    title: 'Пополнение номинального счёта',
    text: 'Пополнение инвестором номинального счёта средствами для участия в сделках. Присоединение инвестора на стороне банка в качестве бенефициара (собственника) средств, находящихся на номинальном счёте.',
  },
  {
    n: 6,
    title: 'Доступ к сделкам на платформе',
    text: 'Инвестор получает полноценный доступ к функционалу и может знакомиться с условиями оферт сделок, выдавать агентские поручения, получать информацию о стадиях взыскания.',
  },
];
const dealSteps = [
  {
    n: 1,
    title: 'Выбор интересующей сделки',
    text: 'Инвестор самостоятельно определяет, в какие из предложенных агентом сделок разместить свои средства.',
  },
  {
    n: 2,
    title: 'Формирование внутренней ЭЦП',
    text: 'Программный комплекс платформы формирует электронную цифровую подпись.',
  },
  {
    n: 3,
    title: 'Выдача агентского поручения',
    text: 'Инвестор подписывает с помощью ЭЦП распоряжение на приобретение выбранного им долгового требования.',
  },
  {
    n: 4,
    title: 'Приобретение требования',
    text: 'Агент-взыскатель исполняет распоряжение, приобретая требование за счёт средств номинального счёта инвестора, находящихся на номинальном счёте.',
  },
  {
    n: 5,
    title: 'Формирование пакета документов по сделке',
    text: 'По итогу приобретения требования платформа формирует полный пакет документов, подтверждающих правоустановку кредиторских прав инвестора.',
  },
  {
    n: 6,
    title: 'Мониторинг взыскания',
    text: 'Инвестор и взыскатель взаимодействуют с помощью онлайн-платформы. Взыскатель размещает отчёты для инвесторов.',
  },
];
const moneyBlocks = [
  {
    icon: 'check',
    title: 'Номинальный счёт в банке',
    text: 'Для любых платежей, связанных со сделками своих клиентов, Кономика использует номинальный счёт. На этом счёте аккумулируются клиентские средства, необходимые для оплаты сделок.',
  },
  {
    icon: 'check',
    title: 'Управление средствами',
    text: 'Инвестор, зачисливший средства на номинальный счет, остается собственником этих средств, согласно cт. 860.1 ГК РФ. Все платежи с данными средствами осуществляются на основании распоряжений, подписанных инвестором с помощью ЭЦП. Копии распоряжений направляются в банк, в котором открыт номинальный счет. Это необходимо, чтобы подтвердить правовое основание платежа и провести транзакцию. Взысканные с должника средства зачисляются исключительно на номинальный счет, таким образом сразу попадая в собственность инвестора. Ни на одном этапе сделки денежные средства инвестора не могут быть использованы Кономикой самостоятельно без имеющегося распоряжения от инвестора.',
  },
  {
    icon: 'check',
    title: 'Доступ к счёту 24/7',
    text: 'В любой момент можно проверить историю операций/транзакций по сделкам, вывести свободные средства.',
  },
  {
    icon: 'warn',
    title: 'Что такое номинальный счёт',
    text: 'Номинальный счёт — особая категория счетов, используемая для хранения средств, права на которые принадлежат третьим лицам. Кономика не имеет технической возможности каким-либо образом использовать средства для целей, не обозначенных в агентском договоре.',
  },
  {
    icon: 'warn',
    title: 'Банковский контроль',
    text: 'Использование номинальных счетов находится под особым банковским контролем. Организация, открывающая номинальный счёт в банке, ограничена в правах распоряжаться средствами на счёте.',
  },
];
const revealDelay = (index: number) => ({ '--reveal-delay': `${index * 120}ms` }) as CSSProperties;

const HowItWorks = () => {
  const isMobile = useWindowWidth();
  const pageRef = useRevealOnScroll<HTMLDivElement>(styles.revealVisible);
  return (
    <div ref={pageRef}>
      <Section className={styles.section}>
        <Container>
          <h1 className={`${styles.title} ${styles.enter}`}>
            Онлайн-платформа Кономика - это современное техническое решение, позволяющее частным
            инвесторам заключать инвестиционные сделки по приобретению долговых требований
          </h1>

          <div className={styles.grid}>
            <div className={`${styles.visual} ${styles.enter} ${styles.enterDelay1}`} aria-hidden>
              <div className={styles.bgCircle} />
              <img className={styles.percent} src={percentImg} alt="" />
              <img className={styles.arrow} src={arrowImg} alt="" />
            </div>
            <div className={`${styles.text} ${styles.enter} ${styles.enterDelay2}`}>
              <div className={styles.caption}>Онлайн-платформа Кономика:</div>
              <ul className={styles.list}>
                <li>не является стороной сделки по приобретению долгового требования</li>
                <li>не привлекает инвестиционные средства в собственный капитал</li>
                <li>
                  предоставляет исключительно техническое решение для прямого взаимодействия
                  инвестора, продавца требования, агента-взыскателя
                </li>
                <li>сделки осуществляются по принципу коллективного софинансирования</li>
                <li>
                  информация о сделках, размещенная агентом на платформе, не является публичной
                  офертой и адресована лицам, заключившим агентский договор.
                </li>
              </ul>
            </div>
          </div>
        </Container>
      </Section>
      <Section>
        <Container>
          <div className={`${styles.diagramSection} ${styles.reveal}`} data-reveal>
            <Image
              src={isMobile ? diagramMobile : diagram}
              alt={''}
              className={styles.diagramImage}
            />
          </div>
        </Container>
      </Section>
      <Section>
        <Container>
          <div className={`${styles.functional__container} ${styles.reveal}`} data-reveal>
            <p id="flow-title" className={styles.functional__lead}>
              Онлайн-платформа Кономика предоставляет удобный функционал взаимодействия между
              инвестором и агентом-взыскателем, который на основании агентского договора
              представляет интересы инвесторов, осуществляя взаимодействие с должником.
            </p>
            <Image
              src={isMobile ? functionalConnectionMobile : functionalConnection}
              alt={''}
              className={styles.functional__image}
            />
          </div>
        </Container>
      </Section>
      <Section className={styles.stepsSection}>
        <Container>
          <h2 className={`${styles.stepsHeading} ${styles.reveal}`} data-reveal>
            Для получения доступа к заключению сделок необходима регистрация на платформе и
            прохождение верификации
          </h2>

          <div className={styles.stepsGrid}>
            {steps.map(({ n, title, text }, idx) => (
              <article
                key={n}
                className={`${styles.stepCard} ${styles.reveal}`}
                style={revealDelay(idx % 3)}
                data-reveal
              >
                <div className={styles.numBadge}>{n}</div>
                <h3 className={styles.stepTitle}>{title}</h3>
                <p className={styles.stepText}>{text}</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>
      <Section className={styles.stepsSection}>
        <Container>
          <h2 className={`${styles.stepsHeading} ${styles.reveal}`} data-reveal>
            Заключение сделки происходит внутри персонального кабинета с использованием внутренней
            ЭЦП
          </h2>

          <div className={styles.stepsGrid}>
            {dealSteps.map(({ n, title, text }, idx) => (
              <article
                key={n}
                className={`${styles.stepCard} ${styles.reveal}`}
                style={revealDelay(idx % 3)}
                data-reveal
              >
                <div className={styles.numBadge}>{n}</div>
                <h3 className={styles.stepTitle}>{title}</h3>
                <p className={styles.stepText}>{text}</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>
      <Section className={styles.stepsSection}>
        <Container>
          <h2 className={`${styles.stepsHeading} ${styles.reveal}`} data-reveal>
            Самостоятельное управление денежными средствами
          </h2>

          <div className={styles.stepsGrid}>
            {moneyBlocks.map(({ icon, title, text }, idx) => (
              <article
                key={idx}
                className={`${styles.stepCard} ${styles.stepCardBox} ${styles.reveal}`}
                style={revealDelay(idx % 3)}
                data-reveal
              >
                <div className={`${styles.numBadge} ${icon === 'check' ? styles.ok : styles.warn}`}>
                  {icon === 'check' ? (
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                      <path
                        d="M20.285 6.709a1 1 0 0 1 0 1.414l-9.19 9.19a1 1 0 0 1-1.414 0L3.715 11.55a1 1 0 1 1 1.414-1.415l5.04 5.04 8.483-8.466a1 1 0 0 1 1.633 0z"
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden>
                      <path d="M11 6h2v9h-2V6zm0 11h2v2h-2v-2z" fill="currentColor" />
                    </svg>
                  )}
                </div>

                <h3 className={styles.stepTitle}>{title}</h3>
                <p className={styles.stepText}>{text}</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>
      <Section>
        <Container>
          <div className={`${styles.fundsMovementSection} ${styles.reveal}`} data-reveal>
            <Image src={fundsMovement} alt={''} className={styles.fundsMovementImage} />
          </div>
        </Container>
      </Section>
    </div>
  );
};

export default HowItWorks;
